import { createContext, useContext, useReducer, useEffect } from "react";
import PropTypes from "prop-types";
import api from "../API/api";

const AuthContext = createContext();

const initialState = {
  user: null,
  isAuthenticated: false,
  isEmailVerified: false,
  isLoading: true,
};

function authReducer(state, action) {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isEmailVerified: action.payload?.is_verified || false,
        isLoading: false,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        isEmailVerified: action.payload?.is_verified || state.isEmailVerified,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isEmailVerified: false,
        isLoading: false,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const validateToken = async () => {
      const userData = localStorage.getItem("user");
      const accessToken = localStorage.getItem("access_token");
      
      if (userData && accessToken) {
        try {
          const user = JSON.parse(userData);
          
          // Validate token by making a test API call
          try {
            await api.get("/userapi");
            dispatch({ type: "SET_USER", payload: user });
          } catch (error) {
            // Token is invalid, clear storage and logout
            console.error("Token validation failed:", error);
            localStorage.removeItem("user");
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            dispatch({ type: "LOGOUT" });
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          dispatch({ type: "LOGOUT" });
        }
      } else {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    validateToken();
  }, []);

  const login = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("access_token", userData.access_token);
    localStorage.setItem("refresh_token", userData.refresh_token);
    dispatch({ type: "SET_USER", payload: userData });
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    dispatch({ type: "LOGOUT" });
  };

  const updateUser = (userData) => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  const value = {
    ...state,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthProvider, useAuth };
